import { Trans } from '@lingui/react'
import { useAtom } from 'jotai'

import { useSubmitConsentCollectionIntroMutation } from '@twisto/api-graphql'
import { FormSubmitButton } from '@twisto/components/atoms/button'
import { useForm } from '@twisto/components/atoms/form'
import type { CastedSubmitHandler } from '@twisto/components/atoms/form'
import {
  Headline,
  HeadlineIcon,
  HeadlineSubtitle,
  HeadlineTitle,
} from '@twisto/components/atoms/headline'
import { Link } from '@twisto/components/atoms/link'
import { Checkbox } from '@twisto/components/molecules/checkbox'
import {
  FlowForm,
  submittingFlowFormAtom,
  useSubmittingWrapper,
} from '@twisto/flow-views/components'
import { BigContractIcon } from '@twisto/icons'

import { useFlow } from '../../flow/flow.context'

import * as styles from './consent-collection-intro-step.styles'

const fieldNames = {
  kbig: 'kbig',
} as const

type FormValues = {
  [fieldNames.kbig]: boolean
}

export const ConsentCollectionIntroStep = () => {
  const { flow } = useFlow<'ConsentCollectionIntroStep'>()
  const [mutate] = useSubmitConsentCollectionIntroMutation()
  const submittingWrapper = useSubmittingWrapper()
  const [submitting] = useAtom(submittingFlowFormAtom)

  const { heading, textParagraphs, kbig } = flow.currentStep

  const formContext = useForm<FormValues>({
    defaultValues: {
      [fieldNames.kbig]: kbig.value ?? false,
    },
  })

  const handleSubmit: CastedSubmitHandler<FormValues> = async (
    values: FormValues
  ) =>
    submittingWrapper(
      mutate({
        variables: {
          auth: {
            sessionId: flow.sessionId,
          },
          input: {
            ...values,
          },
        },
      })
    )

  const consents = [
    {
      label: (
        <Trans
          components={[
            <Link key="0" href={kbig.document.url}>
              {null}
            </Link>,
          ]}
          id="flow.consentCollection.consents.kbig"
        />
      ),
      name: fieldNames.kbig,
      required: kbig.required,
    },
  ]

  return (
    <>
      <Headline>
        <HeadlineIcon>
          <BigContractIcon color="primary" height="100%" width="100%" />
        </HeadlineIcon>
        <HeadlineTitle>{heading}</HeadlineTitle>
        {textParagraphs.map((text, index) => (
          <HeadlineSubtitle key={index}>{text}</HeadlineSubtitle>
        ))}
      </Headline>

      <FlowForm {...formContext} onSubmit={handleSubmit}>
        <div css={styles.checkboxWrapper}>
          {consents.map(({ label, name, required }) => (
            <Checkbox
              key={name}
              label={label}
              name={name}
              required={required}
            />
          ))}
        </div>
        <FormSubmitButton disabled={submitting}>
          <Trans id="flow.consentCollection.submitButton" />
        </FormSubmitButton>
      </FlowForm>
    </>
  )
}
